import { z } from "zod";
import { aiEstimateCostFields, refineAiEstimateCost } from "./ai-model-policy.js";
import { ufSchema } from "./calculation.js";
import { certificationLevelSchema } from "./fiscal-facts.js";
import { LOT_MAX_DATA_ROWS } from "./lot-mapping.js";
import { usdAmountSchema } from "./web-research.js";
/**
 * Calculator "research what is missing" orchestration.
 *
 * A request is one coverage gap of a calculation or workbook upload: route
 * (origin UF → destination UF) × NCM/CEST × the tax families needed. The
 * orchestrator reuses the existing runners and never crawls itself:
 * the IOB browser agent (a tax lookup job) where IOB applies, and a web
 * research sweep (LOCATE / EXTRACT units) for the rest. Results return as
 * fiscal facts and IOB certifications through their existing paths; the
 * request resolves by re-resolving its gap. Nothing here approves, publishes
 * or calculates a rule: UNVERIFIED facts are marked as such and never count
 * as covered for publication-grade results.
 */
/**
 * AUTO: when the deployment flag is on, the server researches the missing gaps
 * of the organization's calculator calculations, and of uploads whose receipt
 * asked `researchMissing: "AUTO"`. ASK: the server never launches; people click
 * "Buscar com agente?" as today.
 */
export const calculatorResearchAutoDispatchSchema = z.enum(["AUTO", "ASK"]);
export const CALCULATOR_RESEARCH_DEFAULT_SETTINGS = {
    enabled: true,
    /** AUTO launches at most this many requests per upload (a typical workbook fits). */
    autoMaxGapsPerUpload: 300,
    /** AUTO launches at most this much estimated spend per upload (US$). */
    autoMaxCostUsdPerUpload: "100",
    /** Admission budget for one attempt (US$); metered Grounding can overshoot. */
    requestMaxCostUsd: "6",
    /** A finished (found, needs review, not found) request is reused this many days. */
    reuseDays: 30,
    /**
     * AUTO: when the deployment flag is on, the server researches missing gaps.
     * ASK: the server never launches; operators click "Buscar com agente?".
     */
    autoDispatch: "AUTO",
};
export const CALCULATOR_RESEARCH_MAX_BATCH_GAPS = 200;
/** Ceiling an administrator may set for the AUTO cap of one upload. */
export const CALCULATOR_RESEARCH_AUTO_MAX_GAPS = 1_000;
/** Attempts before the orchestrator gives up on a request step (FAILED). */
export const CALCULATOR_RESEARCH_MAX_ATTEMPTS = 5;
/** A claimed request not finished within this many seconds is claimable again. */
export const CALCULATOR_RESEARCH_LEASE_SECONDS = 300;
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const signedUsdSchema = z.string().regex(/^-?(0|[1-9]\d{0,15})(\.\d{1,2})?$/);
/** Tax families a gap needs researched. ICMS covers the rates; ICMS_ST the ST regime. */
export const calculatorResearchFamilySchema = z.enum([
    "ICMS",
    "ICMS_ST",
    "IPI",
    "FCP",
    "PIS",
    "COFINS",
]);
/** Forward stored-data reads only; write inputs below remain the legacy two families. */
export const calculatorResearchStoredFamilySchema = z.enum([
    "ICMS",
    "ICMS_ST",
    "IPI",
    "FCP",
    "PIS",
    "COFINS",
]);
/** ASK: an operator clicked "Buscar com agente?". AUTO: launched by the upload's checkbox. */
export const calculatorResearchModeSchema = z.enum(["ASK", "AUTO"]);
/**
 * QUEUED → RUNNING → FOUND | NOT_FOUND | NEEDS_REVIEW | FAILED | CANCELLED.
 * NEEDS_REVIEW (only UNVERIFIED facts) moves forward to FOUND when a reviewer
 * verifies them or a rule is published. No other transition leaves a final state.
 */
export const calculatorResearchStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "FOUND",
    "NOT_FOUND",
    "NEEDS_REVIEW",
    "FAILED",
    "CANCELLED",
]);
export const CALCULATOR_RESEARCH_ACTIVE_STATUSES = ["QUEUED", "RUNNING"];
export const CALCULATOR_RESEARCH_FINAL_STATUSES = [
    "FOUND",
    "NOT_FOUND",
    "NEEDS_REVIEW",
    "FAILED",
    "CANCELLED",
];
export const calculatorResearchStepSchema = z.enum(["PLANNING", "IOB", "WEB", "RESOLVING", "DONE"]);
/** Why a QUEUED request has not launched a lane yet (kill switch, cap or budget). */
export const calculatorResearchBlockSchema = z.enum([
    "ARGON_COST_BOUND_UNAVAILABLE",
    "FEATURE_DISABLED",
    "LANES_PAUSED",
    "WEB_PAUSED",
    "IOB_PAUSED",
    "MONTHLY_BUDGET",
    "DAILY_GROUNDED_CAP",
]);
export const calculatorResearchOriginKindSchema = z.enum(["CALCULATION", "UPLOAD", "OPERATION"]);
export const calculatorResearchStoredOriginKindSchema = z.enum([
    "CALCULATION",
    "UPLOAD",
    "OPERATION",
]);
export const calculatorResearchLaneStatusSchema = z.enum([
    "NOT_PLANNED",
    "QUEUED",
    "RUNNING",
    "DONE",
    "SKIPPED",
    "FAILED",
]);
/** Typed applicability declarations, never instructions for a provider or proof of fiscal eligibility. */
export const calculatorResearchContextSchema = z
    .object({
    calculationDate: z.string().date(),
    transaction: z
        .object({
        kind: z.enum(["PURCHASE", "SALE"]),
        supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR", "UNKNOWN"]),
        recipientIcmsTaxpayer: z.boolean().nullable(),
        goodsOrigin: z
            .string()
            .regex(/^[0-8]$/)
            .nullable(),
        priorSt: z.enum(["PAID", "RETAINED", "UNKNOWN"]),
        federalRegime: z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]),
        /** NCM/CEST alone do not prove weight, ingredients, packaging or industrial equivalence. */
        productQualification: z.literal("UNACCREDITED"),
    })
        .strict()
        .optional(),
})
    .strict();
export const calculatorResearchGapSchema = z
    .object({
    originUf: ufSchema,
    destinationUf: ufSchema,
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    families: z.array(calculatorResearchFamilySchema).min(1).max(6),
    context: calculatorResearchContextSchema.optional(),
})
    .strict();
/** Closed future context is retained for display, never executed by this release. */
export const calculatorResearchStoredContextSchema = z
    .object({
    calculationDate: z.string().date(),
    transaction: z
        .object({
        kind: z.enum(["PURCHASE", "SALE"]),
        supplierType: z.enum(["INDUSTRY", "DISTRIBUTOR", "UNKNOWN"]),
        recipientIcmsTaxpayer: z.boolean().nullable(),
        goodsOrigin: z
            .string()
            .regex(/^[0-8]$/)
            .nullable(),
        priorSt: z.enum(["PAID", "RETAINED", "UNKNOWN"]),
        federalRegime: z.enum(["CUMULATIVE", "NON_CUMULATIVE", "UNKNOWN"]),
        productQualification: z.literal("UNACCREDITED"),
    })
        .strict()
        .optional(),
})
    .strict();
export const calculatorResearchStoredGapSchema = calculatorResearchGapSchema.extend({
    families: z.array(calculatorResearchStoredFamilySchema).min(1).max(6),
    context: calculatorResearchStoredContextSchema.optional(),
});
const gapInputFields = {
    originUf: ufSchema,
    destinationUf: ufSchema,
    ncm: ncmSchema,
    cest: cestSchema.nullish(),
    families: z.array(calculatorResearchFamilySchema).min(1).max(6).optional(),
    context: calculatorResearchContextSchema.optional(),
    /** Worksheet rows or item positions that need this combination. */
    rowRefs: z.array(z.number().int().positive()).max(LOT_MAX_DATA_ROWS).default([]),
};
export const calculatorResearchGapInputSchema = z.object(gapInputFields).strict();
export const calculatorResearchOriginInputSchema = z
    .object({
    kind: calculatorResearchOriginKindSchema,
    id: z.string().uuid(),
    /** The date the calculation or coverage check used. */
    calculationDate: z.string().date(),
})
    .strict();
const createFields = {
    schemaVersion: z.literal("1.0.0"),
    mode: calculatorResearchModeSchema,
    origin: calculatorResearchOriginInputSchema,
    /** Research again although a finished request for the same gap is still fresh. */
    force: z.boolean().default(false),
};
/** `POST /v1/calculator-research/requests` — one gap. */
export const calculatorResearchCreateSchema = z
    .object({ ...createFields, gap: calculatorResearchGapInputSchema })
    .strict();
/** `POST /v1/calculator-research/requests:batch` — up to 200 gaps. */
export const calculatorResearchBatchCreateSchema = z
    .object({
    ...createFields,
    gaps: z.array(calculatorResearchGapInputSchema).min(1).max(CALCULATOR_RESEARCH_MAX_BATCH_GAPS),
    /** Optional SP dashboard proof. Checked atomically on new writes, after idempotent replay. */
    coverageSelection: z
        .object({
        direction: z.enum(["ALL_TO_SP", "SP_TO_ALL"]),
        gaps: z
            .array(z.object({ id: z.string().uuid(), revision: z.number().int().positive() }).strict())
            .min(1)
            .max(10),
    })
        .strict()
        .optional(),
})
    .strict();
/** `POST /v1/calculator-research/requests:estimate` — read only. */
export const calculatorResearchEstimateRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    mode: calculatorResearchModeSchema,
    origin: calculatorResearchOriginInputSchema.optional(),
    gaps: z.array(calculatorResearchGapInputSchema).min(1).max(CALCULATOR_RESEARCH_MAX_BATCH_GAPS),
})
    .strict();
export const calculatorResearchEstimateSchema = z
    .object({
    /** Estimated spend in US dollars (grounded search + extraction); IOB has no per-call cost. */
    ...aiEstimateCostFields,
    etaMinutes: z.number().int().nonnegative(),
    iobLookups: z.number().int().nonnegative(),
    groundedCalls: z.number().int().nonnegative(),
    extractionCalls: z.number().int().nonnegative(),
})
    .strict()
    .superRefine(refineAiEstimateCost);
export const calculatorResearchResolutionSchema = z
    .object({
    outcome: z.enum(["FOUND", "NOT_FOUND", "NEEDS_REVIEW"]),
    /** Weakest level the answer rests on. UNVERIFIED is never publication grade. */
    level: certificationLevelSchema,
    /** PUBLISHED_RULE, CERTIFIED or VERIFIED. UNVERIFIED and NONE are not. */
    publicationGrade: z.boolean(),
    /**
     * The answer changes returned amounts. A published rule does; facts only do
     * when the effective facts mode is ON (SHADOW compares and never changes amounts).
     */
    appliesToResult: z.boolean(),
    /** GENERAL_RULE: the regra geral (sem ST) answers the route (statutory rate, no ST proven). */
    source: z.enum([
        "PUBLISHED_RULE",
        "IOB_CERTIFICATION",
        "WEB_RESEARCH",
        "MIXED",
        "NONE",
        "GENERAL_RULE",
    ]),
    factIds: z.array(z.string().uuid()).max(50),
    missingFields: z.array(z.string().min(1).max(60)).max(10),
    publishedRule: z
        .object({ rulesetVersion: z.string().min(1).max(80), fiscalRuleId: z.string().uuid() })
        .strict()
        .nullable(),
    /** Server revalidation of every linked upload row, independent of a route-only hit. */
    acquisitionQualification: z
        .object({
        // V1 appliance receipts retain their bytes. The additive rows version also
        // covers declared inputs checked against published rules; older strict
        // readers must upgrade before consuming that new version.
        version: z.enum(["APPLIANCE_ACQUISITION_2026_V1", "ACQUISITION_RESEARCH_ROWS_V1"]),
        basisSha256: z.string().regex(/^[a-f0-9]{64}$/),
        rowCount: z.number().int().min(1).max(100000),
        receiptSha256s: z
            .array(z.string().regex(/^[a-f0-9]{64}$/))
            .min(1)
            .max(100000),
    })
        .strict()
        .optional(),
    /** Machine readable summary, e.g. `FACTS_UNVERIFIED_ONLY`, `FACTS_INCOMPLETE`. */
    reasonCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/),
    checkedAt: z.string().datetime(),
})
    .strict();
export const calculatorResearchOriginLinkSchema = z
    .object({
    kind: calculatorResearchStoredOriginKindSchema,
    id: z.string().uuid(),
    mode: calculatorResearchModeSchema,
    /** Legacy bounded preview. Full associations stay server-side for qualification and replay. */
    rowRefs: z.array(z.number().int().positive()).max(500),
    calculationDate: z.string().date(),
    /** This origin caused the request to be created (it counts against the AUTO cap). */
    launchedHere: z.boolean(),
    linkedAt: z.string().datetime(),
})
    .strict();
export const calculatorResearchEventSchema = z
    .object({
    seq: z.number().int().positive(),
    at: z.string().datetime(),
    code: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/),
    step: calculatorResearchStepSchema,
    status: calculatorResearchStatusSchema,
    /** Short factual text, never page content or secrets. */
    detail: z.string().max(300).nullable(),
})
    .strict();
export const calculatorResearchLanesSchema = z
    .object({
    iob: z
        .object({
        status: calculatorResearchLaneStatusSchema,
        /** `tax_lookup_jobs` id the request spawned (links to Agentes → Consultas). */
        lookupJobId: z.string().uuid().nullable(),
        taskId: z.string().uuid().nullable(),
        outcome: z
            .string()
            .regex(/^[A-Z_]{2,30}$/)
            .nullable(),
        startedAt: z.string().datetime().nullable(),
        completedAt: z.string().datetime().nullable(),
    })
        .strict(),
    web: z
        .object({
        status: calculatorResearchLaneStatusSchema,
        /** `research_sweeps` id the request spawned (links to Pesquisa web). */
        sweepId: z.string().uuid().nullable(),
        sweepStatus: z
            .enum(["QUEUED", "RUNNING", "PAUSED", "COMPLETED", "CANCELLED", "BUDGET_EXHAUSTED"])
            .nullable(),
        unitsDone: z.number().int().nonnegative(),
        unitsTotal: z.number().int().nonnegative(),
        startedAt: z.string().datetime().nullable(),
        completedAt: z.string().datetime().nullable(),
    })
        .strict(),
})
    .strict();
export const calculatorResearchRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    gap: calculatorResearchStoredGapSchema,
    mode: calculatorResearchModeSchema,
    status: calculatorResearchStatusSchema,
    step: calculatorResearchStepSchema,
    blocked: calculatorResearchBlockSchema.nullable(),
    estimate: calculatorResearchEstimateSchema,
    actualCostUsd: usdAmountSchema,
    lanes: calculatorResearchLanesSchema,
    resolution: calculatorResearchResolutionSchema.nullable(),
    origins: z.array(calculatorResearchOriginLinkSchema).max(200),
    /** More than one calculation or upload shares this research. */
    shared: z.boolean(),
    attempts: z.number().int().nonnegative(),
    error: z
        .object({ code: z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/), retryable: z.boolean() })
        .strict()
        .nullable(),
    lastEvent: calculatorResearchEventSchema.nullable(),
    /** Only on `GET /requests/{id}`: the audit trail, oldest first. */
    events: z.array(calculatorResearchEventSchema).max(100).optional(),
    correlationId: z.string().min(1).max(200),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    /** State revision; the ETag for cancel. */
    revision: z.number().int().positive(),
})
    .strict();
export const calculatorResearchOutcomeSchema = z.enum([
    "CREATED",
    "JOINED_ACTIVE",
    "REUSED_RESULT",
    "DEFERRED",
]);
export const calculatorResearchDeferralSchema = z.enum([
    "ARGON_COST_BOUND_UNAVAILABLE",
    "AUTO_CAP_GAPS",
    "AUTO_CAP_COST",
    "UPLOAD_NOT_AUTO",
]);
export const calculatorResearchCreateResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z
        .array(z
        .object({
        gap: calculatorResearchGapSchema,
        outcome: calculatorResearchOutcomeSchema,
        deferral: calculatorResearchDeferralSchema.nullable(),
        request: calculatorResearchRequestSchema.nullable(),
    })
        .strict())
        .max(CALCULATOR_RESEARCH_MAX_BATCH_GAPS),
    summary: z
        .object({
        created: z.number().int().nonnegative(),
        joined: z.number().int().nonnegative(),
        reused: z.number().int().nonnegative(),
        deferred: z.number().int().nonnegative(),
        estimatedCostUsd: aiEstimateCostFields.costUsd,
        costUnavailableReason: aiEstimateCostFields.costUnavailableReason,
    })
        .strict()
        .superRefine((value, ctx) => refineAiEstimateCost({ ...value, costUsd: value.estimatedCostUsd }, ctx)),
})
    .strict();
export const calculatorResearchLaneAvailabilitySchema = z.enum([
    "UNQUALIFIED",
    "AVAILABLE",
    "PAUSED",
    "BUDGET",
    "DAILY_CAP",
    "DISABLED",
]);
export const calculatorResearchEstimateResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    items: z
        .array(z
        .object({
        gap: calculatorResearchGapSchema,
        estimate: calculatorResearchEstimateSchema,
        /** An active or fresh request already covers this gap: no new spend. */
        existing: z
            .object({
            requestId: z.string().uuid(),
            status: calculatorResearchStatusSchema,
        })
            .strict()
            .nullable(),
    })
        .strict())
        .max(CALCULATOR_RESEARCH_MAX_BATCH_GAPS),
    total: z
        .object({
        requests: z.number().int().nonnegative(),
        ...aiEstimateCostFields,
        etaMinutes: z.number().int().nonnegative(),
    })
        .strict()
        .superRefine(refineAiEstimateCost),
    caps: z
        .object({
        autoMaxGapsPerUpload: z.number().int().positive(),
        autoMaxCostUsdPerUpload: usdAmountSchema,
        autoRemainingGaps: z.number().int().nonnegative(),
        autoRemainingCostUsd: usdAmountSchema,
    })
        .strict(),
    lanes: z
        .object({
        iob: calculatorResearchLaneAvailabilitySchema,
        web: calculatorResearchLaneAvailabilitySchema,
    })
        .strict(),
})
    .strict();
export const calculatorResearchPageSchema = z
    .object({
    items: z.array(calculatorResearchRequestSchema).max(100),
    nextCursor: z.string().nullable(),
    hasMore: z.boolean(),
})
    .strict();
/** Body of `POST /v1/calculator-research/requests/{id}/cancel` (If-Match: revision). */
export const calculatorResearchCancelSchema = z
    .object({ schemaVersion: z.literal("1.0.0"), reason: z.string().trim().min(3).max(300) })
    .strict();
/* -- Settings -------------------------------------------------------------- */
const settingsFields = {
    /** Operator kill switch for the whole feature. Off: nothing launches, nothing spawns. */
    enabled: z.boolean(),
    autoMaxGapsPerUpload: z.number().int().min(1).max(CALCULATOR_RESEARCH_AUTO_MAX_GAPS),
    autoMaxCostUsdPerUpload: usdAmountSchema,
    requestMaxCostUsd: usdAmountSchema,
    reuseDays: z.number().int().min(1).max(365),
};
export const calculatorResearchSettingsUpdateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ...settingsFields,
    /**
     * Absent keeps the stored value (backward compatible for old clients).
     * AUTO / ASK semantics match `CALCULATOR_RESEARCH_DEFAULT_SETTINGS.autoDispatch`.
     */
    autoDispatch: calculatorResearchAutoDispatchSchema.optional(),
})
    .strict();
export const calculatorResearchSettingsSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /**
     * Deployment flag (`CALCULATOR_RESEARCH_ENABLED`). False: the feature is not
     * deployed in this environment and the UI hides its entry points.
     */
    available: z.boolean(),
    ...settingsFields,
    autoDispatch: calculatorResearchAutoDispatchSchema,
    updatedAt: z.string().datetime().nullable(),
    updatedBy: z.string().uuid().nullable(),
    revision: z.number().int().nonnegative(),
})
    .strict();
/* -- Origin state and recalculation ---------------------------------------- */
export const calculatorResearchRecalcStateSchema = z.enum([
    /** No request on this origin. */
    "NONE",
    /** Research still running. */
    "PENDING",
    /** Evidence found but only UNVERIFIED: amounts stay unchanged until review. */
    "WAITING_REVIEW",
    /** A gap resolved with publication-grade data not yet in a revision. */
    "RECALCULABLE",
    /** Every resolved gap is already part of the latest revision. */
    "UP_TO_DATE",
]);
export const calculatorResearchOriginStateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    kind: calculatorResearchStoredOriginKindSchema,
    id: z.string().uuid(),
    recalcState: calculatorResearchRecalcStateSchema,
    counts: z
        .object({
        total: z.number().int().nonnegative(),
        active: z.number().int().nonnegative(),
        found: z.number().int().nonnegative(),
        needsReview: z.number().int().nonnegative(),
        notFound: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        cancelled: z.number().int().nonnegative(),
    })
        .strict(),
    /** Resolved publication-grade requests not yet in a revision. */
    recalculableRequestIds: z.array(z.string().uuid()).max(500),
    recalculableAt: z.string().datetime().nullable(),
    latestRevision: z
        .object({ calculationId: z.string().uuid(), number: z.number().int().positive() })
        .strict()
        .nullable(),
})
    .strict();
export const calculationRecalculateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    /** Omitted: every recalculable request of the calculation. */
    requestIds: z.array(z.string().uuid()).min(1).max(500).optional(),
})
    .strict();
const amountPairSchema = z
    .object({
    before: z.string().nullable(),
    after: z.string().nullable(),
    /** After minus before; null when either side is unavailable (never against an invented zero). */
    delta: signedUsdSchema.nullable(),
})
    .strict();
export const calculationRevisionChangeSchema = z.enum([
    "COVERAGE",
    "RULE",
    "AMOUNT",
    "STATUS",
    "FACTS_LEVEL",
]);
export const calculationRevisionDiffItemSchema = z
    .object({
    itemId: z.string().min(1),
    position: z.number().int().nonnegative(),
    ncm: ncmSchema.nullable(),
    originUf: ufSchema.nullable(),
    destinationUf: ufSchema.nullable(),
    changes: z.array(calculationRevisionChangeSchema).max(5),
    status: z
        .object({
        before: z.enum(["CALCULATED", "REVIEW_REQUIRED"]),
        after: z.enum(["CALCULATED", "REVIEW_REQUIRED"]),
    })
        .strict(),
    coverage: z
        .object({
        before: z.enum(["EXACT", "NCM_UNIQUE", "MISSING", "AMBIGUOUS"]),
        after: z.enum(["EXACT", "NCM_UNIQUE", "MISSING", "AMBIGUOUS"]),
        /** Facts level the SHADOW comparison reached (null: no comparison stored). */
        factsLevelBefore: certificationLevelSchema.nullable(),
        factsLevelAfter: certificationLevelSchema.nullable(),
    })
        .strict(),
    fiscalRuleId: z
        .object({ before: z.string().uuid().nullable(), after: z.string().uuid().nullable() })
        .strict(),
    landedUnitCost: amountPairSchema,
    taxTotal: amountPairSchema,
    /** Facts would fill the gap but only as UNVERIFIED: not covered, not publication grade. */
    unverifiedOnly: z.boolean(),
    /** Resolved only by facts and the facts mode is SHADOW: shown, never applied to amounts. */
    factsNotApplied: z.boolean(),
})
    .strict();
export const calculationRevisionDiffSchema = z
    .object({
    summary: z
        .object({
        items: z.number().int().nonnegative(),
        itemsChanged: z.number().int().nonnegative(),
        coverageChanged: z.number().int().nonnegative(),
        amountChanged: z.number().int().nonnegative(),
        unverifiedOnly: z.number().int().nonnegative(),
        factsNotApplied: z.number().int().nonnegative(),
        /** Sum of landed unit cost deltas of items that had a value on both sides. */
        landedUnitCostDelta: signedUsdSchema.nullable(),
    })
        .strict(),
    context: z
        .object({
        rulesetBefore: z.string().min(1).max(80),
        rulesetAfter: z.string().min(1).max(80),
        factsMode: z.enum(["OFF", "SHADOW", "ON"]),
    })
        .strict(),
    items: z.array(calculationRevisionDiffItemSchema).max(500),
})
    .strict();
export const calculationRevisionSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    id: z.string().uuid(),
    /** The new, immutable calculation this revision created. */
    calculationId: z.string().uuid(),
    previousCalculationId: z.string().uuid(),
    rootCalculationId: z.string().uuid(),
    /** 2 for the first recalculation of a root calculation. */
    number: z.number().int().min(2),
    requestIds: z.array(z.string().uuid()).max(500),
    diff: calculationRevisionDiffSchema,
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
})
    .strict();
export const calculationRevisionListSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    rootCalculationId: z.string().uuid(),
    items: z.array(calculationRevisionSchema).max(100),
})
    .strict();
//# sourceMappingURL=calculator-research.js.map